import { useState, ChangeEvent, useEffect, useCallback } from 'react';
import { connect, useDispatch } from 'react-redux';
import type { Dispatch } from 'redux';

import { ModalHeader } from 'UIPackage/ModalModule/components/ModalHeader';
import { Modal } from 'UIPackage/ModalModule/components/Modal';
import { TextInput } from 'UIPackage/MainModule/components/TextInput';
import { Button } from 'UIPackage/MainModule/components/Button';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { IKnmReasonChangeTypeSelectOption } from 'CorePackage/CatalogsModule/interfaces/IKnmReasonChangeTypeSelectOption';
import { IKnmReasonChangeType } from 'APIPackage/CatalogsModule/interfaces/IKnmReasonChangeType';
import { SelectInput } from 'UIPackage/MainModule/components/SelectInput';
import {
  geCatalogsKnmPlanReasonChangeTypesSelectOptions,
  getCatalogKnmDocumentTypesSelectOptions,
} from 'CorePackage/CatalogsModule/selectors/catalogsSelectors';
import { CommonCatalogsNamesEnum } from 'CorePackage/CatalogsModule/enums/CommonCatalogsNamesEnum';
import { catalogGet } from 'CorePackage/CatalogsModule/actions/catalogsActions';
import { KnmReasonChangeTypeOperationEnum } from 'APIPackage/CatalogsModule/enums/KnmReasonChangeTypeOperationEnum';
import { addNoticeAction } from 'CorePackage/NotificationModule/actions/NoticeActions';
import { NoticeTypesEnum } from 'CorePackage/NotificationModule/enums/NoticeTypesEnum';
import { MessageTypesEnum } from 'CorePackage/NotificationModule/enums/MessageTypesEnum';
import { convertFileToBase64 } from 'KnmPackage/KnmDocumentsModule/common/convertFileToBase64';
import { PASS_SIGNATURE_VALIDATION, INVALID_DOCUMENT_SIGNATURE } from 'CorePackage/MainModule/constants/notices';
import { useKnmDocumentAttachment } from 'KnmPackage/MainModule/hooks/useKnmDocumentAttachment';
import { KnmAttachments } from 'KnmPackage/KnmDocumentsModule/components/KnmAttachments';
import { IKnmDocumentRequest } from 'APIPackage/KnmModule/interfaces/IKnmDocumentRequest';
import { IKnmDocumentTypeSelectOption } from 'CorePackage/CatalogsModule/interfaces/IKnmDocumentTypeSelectOption';
import { convertSignFileToBase64 } from 'KnmPackage/KnmDocumentsModule/common/convertSignFileToBase64';
import { planListLinkKnmToPlan } from 'PlanListPackage/MainModule/actions/planListActions';
import { IKnmAttachment } from 'APIPackage/KnmModule/interfaces/IKnmAttachment';
import { ModalBody } from 'UIPackage/ModalModule/components/ModalBody';
import { ModalActions } from 'UIPackage/ModalModule/components/ModalActions';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';

import styles from './PlanAddModal.module.scss';

interface IPlanAddModalProps {
  planId: number;
  visible: boolean;
  isErknm: boolean;
  isKnm?: boolean;
  onClose: () => void;
  onApply?: (selectedReason: IKnmReasonChangeType, documents: IKnmDocumentRequest) => void;
}

interface IPlanAddModalStoreProps {
  knmPlanReasonChangeOptions: IKnmReasonChangeTypeSelectOption[];
  knmDocumentTypesSelectOptions: IKnmDocumentTypeSelectOption[];
}

const PlanAddModalWithoutStore = ({
  visible,
  planId,
  isErknm,
  isKnm,
  onClose,
  onApply,
  knmDocumentTypesSelectOptions,
  knmPlanReasonChangeOptions,
}: IPlanAddModalProps & IPlanAddModalStoreProps) => {
  const [inputValue, setInputValue] = useState('');
  const [document, setDocument] = useState<File | null>(null);
  const [signs, setSigns] = useState<Array<File>>([]);
  const [selectValue, setSelectValue] = useState<IKnmReasonChangeTypeSelectOption | null>(null);
  const { uploadDocumentSignatures, uploadKnmDocumentAttachment } = useKnmDocumentAttachment();
  const disableButton = isErknm ? selectValue && document : planId && inputValue;
  const attachmentType = knmDocumentTypesSelectOptions?.find(el => el.code === 'TYPE_LV');
  const knmPlanReasonIncludeOptions = knmPlanReasonChangeOptions.filter(
    option => option.operation === KnmReasonChangeTypeOperationEnum.INCLUDE,
  );

  const dispatch = useDispatch<Dispatch<RootActions>>();

  useEffect(() => {
    dispatch(catalogGet(CommonCatalogsNamesEnum.KNM_PLAN_REASON_CHANGE_TYPES));
    dispatch(catalogGet(CommonCatalogsNamesEnum.KNM_DOCUMENT_TYPES));
  }, [dispatch]);

  const handleAfterClose = useCallback(() => {
    setInputValue('');
    setSelectValue(null);
    setDocument(null);
    setSigns([]);
  }, []);

  const handleSaveButton = useCallback(() => {
    if (planId && inputValue) {
      dispatch(planListLinkKnmToPlan(planId, inputValue));
    }
    onClose();
  }, [dispatch, inputValue, onClose, planId]);

  const handleSaveModalButton = useCallback(async () => {
    if (onApply && isErknm && selectValue) {
      const reason = {
        id: selectValue.id,
        title: selectValue.title,
        operation: selectValue.operation,
        code: selectValue.code,
        enabled: selectValue.enabled,
        weight: selectValue.weight,
      };

      if (document && signs.length) {
        const convertedDocument = await convertFileToBase64(document);
        const signActions = signs.map(async sign => {
          const signText = await convertSignFileToBase64(sign);

          return signText;
        });
        const signTextArr = await Promise.all(signActions);
        const files = {
          document: convertedDocument,
          signatures: signTextArr,
        };
        const res = await uploadDocumentSignatures(files);

        if (res && res.signed) {
          const attachments = await uploadKnmDocumentAttachment(document);

          dispatch(
            addNoticeAction({
              renderContent: PASS_SIGNATURE_VALIDATION,
              noticeType: NoticeTypesEnum.SUCCESS,
              messageType: MessageTypesEnum.VALIDATE_DOCUMENT,
            }),
          );

          if (attachments) {
            const signedDocument: IKnmAttachment = {
              ...attachments,
              signatureInfo: res.signatureInfo,
              signed: true,
            };

            if (attachmentType && res.signatureInfo && attachments) {
              onApply(reason, { type: attachmentType, attachments: [signedDocument] });
            }
          }
        } else {
          dispatch(
            addNoticeAction({
              renderContent: INVALID_DOCUMENT_SIGNATURE,
              noticeType: NoticeTypesEnum.ERROR,
              messageType: MessageTypesEnum.VALIDATE_DOCUMENT,
            }),
          );
        }
      } else if (document && attachmentType) {
        const attachments = await uploadKnmDocumentAttachment(document);

        attachments && onApply(reason, { type: attachmentType, attachments: [attachments] });
      }
    } else {
      handleSaveButton();
    }
  }, [
    attachmentType,
    dispatch,
    document,
    handleSaveButton,
    isErknm,
    onApply,
    selectValue,
    signs,
    uploadDocumentSignatures,
    uploadKnmDocumentAttachment,
  ]);

  const handleChangeInput = useCallback((e: ChangeEvent<HTMLInputElement>) => setInputValue(e.target.value), []);

  const handleChangeSelect = useCallback((value: IKnmReasonChangeTypeSelectOption) => setSelectValue(value), []);

  const handleRemoveDocument = useCallback(() => {
    setDocument(null);
  }, []);

  const handleRemoveSign = useCallback(
    (index: number) => () => {
      setSigns(prev => prev.filter((el: File, idx: number) => idx !== index));
    },
    [],
  );

  const handleSetAttachment = useCallback((file: File, isSign: boolean) => {
    isSign ? setSigns(prev => [...prev, file]) : setDocument(file);
  }, []);

  return (
    <Modal modalBodyClassName={styles.ModalBody} visible={visible} onClose={onClose} afterClose={handleAfterClose}>
      <ModalHeader title="Добавление КНМ в план" sizeText="small" color="black" />
      <ModalBody size="content" border="bottom">
        <div className={styles.InputBlock}>
          <div className={styles.InputContainer}>
            {!isKnm && (
              <TextInput
                className={styles.Input}
                placeholder="Введите учетный номер КНМ"
                onChange={handleChangeInput}
                value={inputValue}
              />
            )}
            {isErknm && (
              <>
                <SelectInput
                  className={styles.Select}
                  placeholder="Тип"
                  options={knmPlanReasonIncludeOptions}
                  size="medium"
                  value={selectValue}
                  onChange={handleChangeSelect}
                />
                <KnmAttachments
                  document={document}
                  signs={signs}
                  handleRemoveDocument={handleRemoveDocument}
                  handleRemoveSign={handleRemoveSign}
                  handleSetAttachment={handleSetAttachment}
                />
              </>
            )}
          </div>
        </div>
      </ModalBody>
      <ModalActions>
        <Button text="Закрыть" size="medium" onClick={onClose} />
        <Button text="Добавить" size="medium" disabled={!disableButton} onClick={handleSaveModalButton} />
      </ModalActions>
    </Modal>
  );
};

const mapStateToProps = (state: IRootState): IPlanAddModalStoreProps => ({
  knmPlanReasonChangeOptions: geCatalogsKnmPlanReasonChangeTypesSelectOptions(state),
  knmDocumentTypesSelectOptions: getCatalogKnmDocumentTypesSelectOptions(state),
});

export const PlanAddModal = connect(mapStateToProps)(PlanAddModalWithoutStore);
